import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { 
  CREATE_INVALIDATION_STRATEGY_TEMPLATE, 
  UPDATE_INVALIDATION_STRATEGY_TEMPLATE 
} from '../graphql/mutations/strategyTemplates';
import toast from 'react-hot-toast';

const InvalidationStrategyTemplateModal = ({ template, onClose }) => {
  const isEditing = !!template;
  
  // Form state
  const [name, setName] = useState('');
  const [priceBasedEnabled, setPriceBasedEnabled] = useState(false);
  const [priceDropPercentage, setPriceDropPercentage] = useState('30');
  const [timeBasedEnabled, setTimeBasedEnabled] = useState(false);
  const [timeDurationDays, setTimeDurationDays] = useState(90);
  
  const [saving, setSaving] = useState(false);

  // Mutations
  const [createTemplate] = useMutation(CREATE_INVALIDATION_STRATEGY_TEMPLATE);
  const [updateTemplate] = useMutation(UPDATE_INVALIDATION_STRATEGY_TEMPLATE);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Load template data if editing
  useEffect(() => {
    if (template) {
      setName(template.name || '');
      setPriceBasedEnabled(template.priceBasedEnabled || false);
      setPriceDropPercentage(template.priceDropPercentage || '30');
      setTimeBasedEnabled(template.timeBasedEnabled || false);
      setTimeDurationDays(template.timeDurationDays || 90);
    }
  }, [template]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!priceBasedEnabled && !timeBasedEnabled) {
      toast.error('At least one invalidation condition must be enabled');
      return;
    }

    setSaving(true);
    
    const input = {
      name: name.trim(),
      priceBasedEnabled,
      priceDropPercentage: priceBasedEnabled ? priceDropPercentage : null,
      timeBasedEnabled,
      timeDurationDays: timeBasedEnabled ? timeDurationDays : null,
    };

    try {
      if (isEditing) {
        const { data } = await updateTemplate({ variables: { id: template.id, input } });
        if (data?.updateInvalidationStrategyTemplate?.success) {
          toast.success('Template updated');
          onClose();
        } else {
          toast.error(data?.updateInvalidationStrategyTemplate?.error || 'Failed to update');
        }
      } else {
        const { data } = await createTemplate({ variables: { input } });
        if (data?.createInvalidationStrategyTemplate?.success) {
          toast.success('Template created');
          onClose();
        } else {
          toast.error(data?.createInvalidationStrategyTemplate?.error || 'Failed to create');
        }
      }
    } catch (err) {
      toast.error(err.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Invalidation Strategy' : 'New Invalidation Strategy'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., High Risk Invalidation"
                required
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Note:</strong> When both conditions are enabled, the position will be flagged if <em>either</em> condition is met (OR logic).
              </p>
            </div>

            {/* Price-based Invalidation */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={priceBasedEnabled}
                  onChange={(e) => setPriceBasedEnabled(e.target.checked)}
                  className="mr-3 w-5 h-5"
                />
                <span className="font-medium text-gray-900 dark:text-white">Price-based Invalidation</span>
              </label>
              {priceBasedEnabled && (
                <div className="ml-8">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Trigger when price drops more than X% from average buy price
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 dark:text-gray-400">Drop more than</span>
                    <input
                      type="number"
                      value={priceDropPercentage}
                      onChange={(e) => setPriceDropPercentage(e.target.value)}
                      className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min="5"
                      max="90"
                    />
                    <span className="text-gray-600 dark:text-gray-400">%</span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="90"
                    value={priceDropPercentage}
                    onChange={(e) => setPriceDropPercentage(e.target.value)}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5%</span>
                    <span>90%</span>
                  </div>
                </div>
              )}
            </div>

            {/* Time-based Invalidation */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={timeBasedEnabled}
                  onChange={(e) => setTimeBasedEnabled(e.target.checked)}
                  className="mr-3 w-5 h-5"
                />
                <span className="font-medium text-gray-900 dark:text-white">Time-based Invalidation</span>
              </label>
              {timeBasedEnabled && (
                <div className="ml-8">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Trigger when position is held longer than X days
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 dark:text-gray-400">Held longer than</span>
                    <input
                      type="number"
                      value={timeDurationDays}
                      onChange={(e) => setTimeDurationDays(parseInt(e.target.value))}
                      className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min="1"
                    />
                    <span className="text-gray-600 dark:text-gray-400">days</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {[30, 90, 180, 365].map(days => (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setTimeDurationDays(days)}
                        className={`px-3 py-1 text-sm rounded ${
                          timeDurationDays === days
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {days < 365 ? `${days}d` : '1y'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : isEditing ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvalidationStrategyTemplateModal;
